import supabase from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

// Local storage keys
const STORAGE_KEY = 'nakamoto-messages';
const ADMIN_KEY = 'nakamoto-admin-auth';

// Save message to both Supabase and local storage
export const saveMessage = async (message) => {
  try {
    // Create a unique ID for the message using UUID
    const messageId = uuidv4();
    
    // Prepare the message object for Supabase
    const newMessage = {
      id: messageId,
      title: message.title || null,
      content: message.content,
      sender: message.sender || 'Anonymous',
      is_anonymous: message.isAnonymous,
      timestamp: new Date().toISOString(),
      approved: false,
      flagged: false
    };

    // Save to Supabase
    const { error } = await supabase
      .from('kindness_messages_x7y9z2')
      .insert([newMessage]);

    if (error) {
      console.error('Error saving to Supabase:', error);
      // Fall back to local storage if Supabase fails
      return fallbackToLocalStorage(message, messageId);
    }

    // Also save locally for immediate display
    const localMessage = {
      ...message,
      id: messageId,
      timestamp: new Date().toISOString(),
      approved: true, // Auto-approve local messages for better UX
      flagged: false
    };
    
    const messages = getLocalMessages();
    messages.push(localMessage);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    
    return messageId;
  } catch (err) {
    console.error('Failed to save message:', err);
    // Fall back to local storage only
    return fallbackToLocalStorage(message);
  }
};

// Fallback function for saving to local storage only
const fallbackToLocalStorage = (message, id = null) => {
  const messageId = id || uuidv4();
  const localMessage = {
    ...message,
    id: messageId,
    timestamp: new Date().toISOString(),
    approved: true,
    flagged: false
  };
  
  const messages = getLocalMessages();
  messages.push(localMessage);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  
  return messageId;
};

// Get messages from local storage
export const getLocalMessages = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
};

// Get all messages (combines Supabase and local storage)
export const getMessages = async () => {
  try {
    // Try to get from Supabase first
    const { data: supabaseMessages, error } = await supabase
      .from('kindness_messages_x7y9z2')
      .select('*')
      .order('timestamp', { ascending: false });
    
    if (error) {
      console.error('Error fetching from Supabase:', error);
      // Fall back to local storage
      return getLocalMessages();
    }
    
    // Format Supabase messages to match local storage format
    const formattedMessages = supabaseMessages.map(msg => ({
      id: msg.id,
      title: msg.title,
      content: msg.content,
      sender: msg.sender,
      isAnonymous: msg.is_anonymous,
      approved: msg.approved,
      flagged: msg.flagged,
      timestamp: msg.timestamp
    }));
    
    // Merge with local messages (for offline support)
    const localMessages = getLocalMessages();
    
    // Create a Set of IDs from Supabase messages
    const supabaseIds = new Set(formattedMessages.map(msg => msg.id));
    
    // Filter local messages to only include those not in Supabase
    const uniqueLocalMessages = localMessages.filter(msg => !supabaseIds.has(msg.id));
    
    // Return combined messages
    return [...formattedMessages, ...uniqueLocalMessages];
  } catch (err) {
    console.error('Failed to fetch messages:', err);
    // Fall back to local storage
    return getLocalMessages();
  }
};

// Get a specific message by ID
export const getMessage = async (id) => {
  try {
    // Try Supabase first
    const { data: message, error } = await supabase
      .from('kindness_messages_x7y9z2')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error || !message) {
      // Fall back to local storage
      const localMessages = getLocalMessages();
      return localMessages.find(msg => msg.id === id);
    }
    
    // Format Supabase message to match local storage format
    return {
      id: message.id,
      title: message.title,
      content: message.content,
      sender: message.sender,
      isAnonymous: message.is_anonymous,
      approved: message.approved,
      flagged: message.flagged,
      timestamp: message.timestamp
    };
  } catch (err) {
    console.error('Failed to fetch message:', err);
    // Fall back to local storage
    const localMessages = getLocalMessages();
    return localMessages.find(msg => msg.id === id);
  }
};

// Update a message
export const updateMessage = async (id, updates) => {
  try {
    // Format updates for Supabase
    const supabaseUpdates = {
      ...(updates.title !== undefined && { title: updates.title }),
      ...(updates.content !== undefined && { content: updates.content }),
      ...(updates.sender !== undefined && { sender: updates.sender }),
      ...(updates.isAnonymous !== undefined && { is_anonymous: updates.isAnonymous }),
      ...(updates.approved !== undefined && { approved: updates.approved }),
      ...(updates.flagged !== undefined && { flagged: updates.flagged })
    };
    
    // Update in Supabase
    const { error } = await supabase
      .from('kindness_messages_x7y9z2')
      .update(supabaseUpdates)
      .eq('id', id);
    
    if (error) {
      console.error('Error updating in Supabase:', error);
    }
    
    // Also update in local storage
    const messages = getLocalMessages();
    const index = messages.findIndex(msg => msg.id === id);
    if (index !== -1) {
      messages[index] = { ...messages[index], ...updates };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
  } catch (err) {
    console.error('Failed to update message:', err);
    // Fall back to local storage only
    const messages = getLocalMessages();
    const index = messages.findIndex(msg => msg.id === id);
    if (index !== -1) {
      messages[index] = { ...messages[index], ...updates };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
  }
};

// Delete a message
export const deleteMessage = async (id) => {
  try {
    // Delete from Supabase
    const { error } = await supabase
      .from('kindness_messages_x7y9z2')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting from Supabase:', error);
    }
    
    // Also delete from local storage
    const messages = getLocalMessages();
    const filtered = messages.filter(msg => msg.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (err) {
    console.error('Failed to delete message:', err);
    // Fall back to local storage only
    const messages = getLocalMessages();
    const filtered = messages.filter(msg => msg.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  }
};

// Get approved messages
export const getApprovedMessages = async () => {
  try {
    // Get from Supabase
    const { data: supabaseMessages, error } = await supabase
      .from('kindness_messages_x7y9z2')
      .select('*')
      .eq('approved', true)
      .eq('flagged', false)
      .order('timestamp', { ascending: false });
    
    if (error) {
      console.error('Error fetching approved messages from Supabase:', error);
      // Fall back to local storage
      return getLocalMessages().filter(msg => msg.approved && !msg.flagged);
    }
    
    // Format Supabase messages
    const formattedMessages = supabaseMessages.map(msg => ({
      id: msg.id,
      title: msg.title,
      content: msg.content,
      sender: msg.sender,
      isAnonymous: msg.is_anonymous,
      approved: msg.approved,
      flagged: msg.flagged,
      timestamp: msg.timestamp
    }));
    
    // Also include locally approved messages that aren't in Supabase
    const localMessages = getLocalMessages().filter(msg => msg.approved && !msg.flagged);
    const supabaseIds = new Set(formattedMessages.map(msg => msg.id));
    const uniqueLocalMessages = localMessages.filter(msg => !supabaseIds.has(msg.id));
    
    return [...formattedMessages, ...uniqueLocalMessages];
  } catch (err) {
    console.error('Failed to fetch approved messages:', err);
    // Fall back to local storage
    return getLocalMessages().filter(msg => msg.approved && !msg.flagged);
  }
};

// Admin authentication helpers
export const setAdminAuth = (isAuthenticated) => {
  localStorage.setItem(ADMIN_KEY, JSON.stringify(isAuthenticated));
};

export const getAdminAuth = () => {
  const stored = localStorage.getItem(ADMIN_KEY);
  return stored ? JSON.parse(stored) : false;
};

// Function to migrate local messages to Supabase
export const migrateLocalMessagesToSupabase = async () => {
  try {
    const localMessages = getLocalMessages();
    if (localMessages.length === 0) return { migrated: 0 };
    
    // Format messages for Supabase
    const messagesToMigrate = localMessages.map(msg => ({
      id: msg.id,
      title: msg.title || null,
      content: msg.content,
      sender: msg.sender || 'Anonymous',
      is_anonymous: msg.isAnonymous !== undefined ? msg.isAnonymous : true,
      timestamp: msg.timestamp || new Date().toISOString(),
      approved: msg.approved !== undefined ? msg.approved : true,
      flagged: msg.flagged !== undefined ? msg.flagged : false
    }));
    
    // Insert messages in batches to avoid payload limits
    const batchSize = 20;
    let migratedCount = 0;
    
    for (let i = 0; i < messagesToMigrate.length; i += batchSize) {
      const batch = messagesToMigrate.slice(i, i + batchSize);
      const { error } = await supabase
        .from('kindness_messages_x7y9z2')
        .upsert(batch, { onConflict: 'id' });
      
      if (!error) migratedCount += batch.length;
    }
    
    return { migrated: migratedCount };
  } catch (err) {
    console.error('Failed to migrate messages:', err);
    return { error: err.message };
  }
};